import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { format, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import {
  CalendarDays, Home, Search, Eye, Stamp, Users2, AlarmClock, Ban,
} from "lucide-react";
import { isLightColor, initialsOf } from "@/lib/agendaTipi";

const HOUR_START = 7;
const HOUR_END = 22;
const TOTAL_MIN = (HOUR_END - HOUR_START) * 60;
const PX = 1; // px per minute -> 60px/hour
const GRID_H = TOTAL_MIN * PX;
const SNAP = 15;
const TIME_COL = 54;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const snap = (m) => Math.round(m / SNAP) * SNAP;
const gridMinOf = (iso) => { const d = new Date(iso); return (d.getHours() * 60 + d.getMinutes()) - HOUR_START * 60; };
const hhmm = (gridMin) => {
  const t = HOUR_START * 60 + gridMin;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
const dayAtGridMin = (day, gridMin) => {
  const d = new Date(day); d.setHours(HOUR_START, 0, 0, 0); d.setMinutes(gridMin); return d;
};
// Naive local wall-clock string (no tz); backend interprets it as Europe/Rome.
const toWall = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
};

const TIPO_ICON = {
  vendita: Stamp, acquisizione: Home, sopralluogo: Search, acquirente: Users2, bloccato: Ban,
  proposta_scadenza: AlarmClock, visita: Eye, visita_scheda: Eye,
};
const iconFor = (e) => TIPO_ICON[e.tipo] || TIPO_ICON[e.kind] || CalendarDays;

// Greedy column packing for overlapping events within a single day.
function packDay(items) {
  const arr = items
    .map((e) => ({ e, s: clamp(gridMinOf(e.data), 0, TOTAL_MIN), en: clamp(gridMinOf(e.data) + (e.durata_min || 60), SNAP, TOTAL_MIN) }))
    .sort((a, b) => a.s - b.s || a.en - b.en);
  const out = [];
  let cluster = [], clusterEnd = -1;
  const flush = () => {
    if (!cluster.length) return;
    const colsEnd = [];
    cluster.forEach((it) => {
      let ci = colsEnd.findIndex((end) => end <= it.s);
      if (ci === -1) { ci = colsEnd.length; colsEnd.push(it.en); } else colsEnd[ci] = it.en;
      it.col = ci;
    });
    const ncols = colsEnd.length;
    cluster.forEach((it) => { it.ncols = ncols; out.push(it); });
    cluster = []; clusterEnd = -1;
  };
  arr.forEach((it) => {
    if (cluster.length && it.s >= clusterEnd) flush();
    cluster.push(it); clusterEnd = Math.max(clusterEnd, it.en);
  });
  flush();
  return out;
}

export function AgendaTimeGrid({ days, events, colorOf, nameById, onEventClick, onCreate, onMove, onResize, canEdit, scrollKey, compact }) {
  const scrollRef = useRef(null);
  const colRefs = useRef([]);
  const [drag, setDrag] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    const n = new Date();
    const h = days.some((d) => isSameDay(d, n)) ? Math.max(HOUR_START, n.getHours() - 1) : 8;
    scrollRef.current.scrollTop = (h - HOUR_START) * 60 * PX;
  }, [scrollKey]); // eslint-disable-line

  const timedByDay = days.map((d) => events.filter((e) => !e.all_day && e.data && isSameDay(new Date(e.data), d)));
  const allDayByDay = days.map((d) => events.filter((e) => e.all_day && e.data && isSameDay(new Date(e.data), d)));

  const yToGridMin = (clientY, dayIndex) => {
    const el = colRefs.current[dayIndex];
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return clamp(snap((clientY - r.top) / PX), 0, TOTAL_MIN);
  };
  const dayIndexAtX = (clientX) => {
    for (let i = 0; i < colRefs.current.length; i++) {
      const el = colRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right) return i;
    }
    return null;
  };

  const dragRef = useRef(null);
  const moveRef = useRef(null);
  const upRef = useRef(null);
  const moveStable = useRef((e) => moveRef.current && moveRef.current(e)).current;
  const upStable = useRef((e) => upRef.current && upRef.current(e)).current;
  const noScroll = useRef((ev) => ev.preventDefault()).current;
  const startListeners = () => {
    window.addEventListener("pointermove", moveStable);
    window.addEventListener("pointerup", upStable);
    window.addEventListener("pointercancel", cancelStable);
  };
  const cancelStable = useRef(() => {
    window.removeEventListener("pointermove", moveStable);
    window.removeEventListener("pointerup", upStable);
    window.removeEventListener("pointercancel", cancelStable);
    window.removeEventListener("touchmove", noScroll);
    dragRef.current = null;
    setDrag(null);
  }).current;
  const beginDrag = (next) => { dragRef.current = next; setDrag(next); };
  moveRef.current = (ev) => {
    const d = dragRef.current;
    if (!d) return;
    const moved = d.moved || Math.abs(ev.clientY - d.pointerStartY) > 4 || Math.abs(ev.clientX - d.pointerStartX) > 4;
    let next;
    if (d.kind === "create") {
      const end = yToGridMin(ev.clientY, d.dayIndex);
      next = { ...d, endMin: Math.max(end, d.startMin + SNAP), moved: true };
    } else if (d.kind === "resize") {
      const end = yToGridMin(ev.clientY, d.dayIndex);
      next = { ...d, endMin: Math.max(end, d.startMin + SNAP), moved };
    } else {
      const di = (days.length > 1 ? dayIndexAtX(ev.clientX) : 0) ?? d.dayIndex;
      const topMin = clamp(snap((ev.clientY - (colRefs.current[di]?.getBoundingClientRect().top || 0)) / PX - d.grabOffset), 0, TOTAL_MIN - d.dur);
      next = { ...d, kind: d.kind === "click" ? "click" : "move", dayIndex: di, startMin: topMin, endMin: topMin + d.dur, moved };
    }
    dragRef.current = next;
    setDrag(next);
  };
  upRef.current = () => {
    window.removeEventListener("pointermove", moveStable);
    window.removeEventListener("pointerup", upStable);
    window.removeEventListener("pointercancel", cancelStable);
    window.removeEventListener("touchmove", noScroll);
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d) return;
    if (d.kind === "create") {
      const durMin = Math.max(d.endMin - d.startMin, SNAP);
      onCreate && onCreate({ startISO: toWall(dayAtGridMin(days[d.dayIndex], d.startMin)), durMin: d.moved ? durMin : 60 });
    } else if (!d.moved) {
      onEventClick && onEventClick(d.event);
    } else if (d.kind === "move") {
      onMove && onMove(d.event, toWall(dayAtGridMin(days[d.dayIndex], d.startMin)));
    } else if (d.kind === "resize") {
      onResize && onResize(d.event, Math.max(snap(d.startMin + (d.endMin - d.startMin)) - d.startMin, SNAP));
    }
  };

  const bgPointerDown = (dayIndex) => (ev) => {
    if (ev.button !== 0 || !onCreate) return;
    const startMin = yToGridMin(ev.clientY, dayIndex);
    beginDrag({ kind: "create", dayIndex, startMin, endMin: startMin + 30, moved: false, pointerStartY: ev.clientY, pointerStartX: ev.clientX });
    startListeners();
  };
  const blockPointerDown = (e, dayIndex, startMin, dur) => (ev) => {
    ev.stopPropagation();
    if (ev.button !== 0) return;
    const editable = canEdit && canEdit(e);
    if (ev.pointerType !== "touch") ev.preventDefault();
    const grab = (ev.clientY - (colRefs.current[dayIndex]?.getBoundingClientRect().top || 0)) / PX - startMin;
    const base = { event: e, dayIndex, startMin, dur, endMin: startMin + dur, grabOffset: clamp(grab, 0, dur), moved: false, pointerStartY: ev.clientY, pointerStartX: ev.clientX };
    if (ev.pointerType === "touch" && editable) {
      // Mobile: tieni premuto per trascinare; un movimento veloce resta scorrimento.
      const x0 = ev.clientX, y0 = ev.clientY;
      const stop = () => { clearTimeout(t); window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", stop); };
      const mv = (m) => { if (Math.abs(m.clientX - x0) > 8 || Math.abs(m.clientY - y0) > 8) stop(); };
      const up = () => { stop(); onEventClick && onEventClick(e); };
      const t = setTimeout(() => {
        stop();
        if (navigator.vibrate) navigator.vibrate(30);
        window.addEventListener("touchmove", noScroll, { passive: false });
        beginDrag({ ...base, kind: "move", moved: true });
        startListeners();
      }, 450);
      window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", stop);
      return;
    }
    beginDrag({ ...base, kind: editable ? "move" : "click" });
    startListeners();
  };
  const resizePointerDown = (e, dayIndex, startMin, dur) => (ev) => {
    ev.stopPropagation();
    if (ev.button !== 0) return;
    ev.preventDefault();
    beginDrag({ kind: "resize", event: e, dayIndex, startMin, dur, endMin: startMin + dur, moved: false, pointerStartY: ev.clientY, pointerStartX: ev.clientX });
    startListeners();
  };

  const hours = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);
  const colW = `calc((100% - ${TIME_COL}px) / ${days.length})`;

  return (
    <div className="rounded-3xl border border-[var(--border)] bg-white overflow-hidden" data-testid="agenda-timegrid">
      {/* Header */}
      <div className="flex border-b border-slate-100">
        <div style={{ width: TIME_COL }} className="shrink-0" />
        {days.map((d) => {
          const today = isSameDay(d, new Date());
          return (
            <div key={d.toISOString()} data-testid={`tg-head-${format(d, "yyyy-MM-dd")}`}
                 className="flex-1 min-w-0 text-center pt-3 pb-2">
              <div className={`text-[11px] font-bold tracking-wider ${today ? "text-[#B8369F]" : "text-slate-500"}`}>{format(d, "EEE", { locale: it }).toUpperCase()}</div>
              <div className={`mx-auto mt-1 w-11 h-11 rounded-full flex items-center justify-center font-display font-extrabold text-2xl ${today ? "bg-[#FC82ED] text-[#2B2B2B]" : "text-[#2B2B2B]"}`} data-testid={today ? "tg-today-num" : undefined}>{format(d, "d")}</div>
            </div>
          );
        })}
      </div>

      {/* All-day row */}
      <div className="flex border-b border-slate-200 min-h-[30px]" data-testid="tg-allday-row">
        <div style={{ width: TIME_COL }} className="shrink-0 flex items-center justify-end pr-2 text-[9px] font-semibold text-slate-400 leading-tight text-right">Tutto il<br/>giorno</div>
        {days.map((d, di) => (
          <div key={d.toISOString()} className="flex-1 min-w-0 border-l border-slate-100 p-1 space-y-1">
            {allDayByDay[di].map((e) => {
              const c = colorOf(e);
              const Ico = iconFor(e);
              return (
                <button key={e.id} data-testid={`tg-allday-${e.id}`} onClick={() => onEventClick && onEventClick(e)}
                        className="w-full flex items-center gap-1 text-[11px] font-semibold truncate rounded-md px-2 py-0.5 text-left text-white hover:brightness-95"
                        style={{ background: c }}>
                  <Ico size={11} className="shrink-0" />
                  <span className="truncate">{e.titolo}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="overflow-y-auto relative" style={{ maxHeight: compact ? 520 : "max(440px, calc(100vh - 300px))" }}>
        <div className="flex relative" style={{ height: GRID_H + 8 }}>
          {/* time gutter */}
          <div style={{ width: TIME_COL }} className="shrink-0 relative">
            {hours.map((h, i) => (
              <div key={h} className="absolute right-2 -translate-y-1/2 text-[10px] text-slate-400" style={{ top: i * 60 * PX }}>
                {i === 0 ? "" : `${h}:00`}
              </div>
            ))}
          </div>
          {/* day columns */}
          {days.map((d, di) => {
            const packed = packDay(timedByDay[di]);
            const today = isSameDay(d, new Date());
            const nowMin = gridMinOf(now.toISOString());
            return (
              <div key={d.toISOString()} ref={(el) => (colRefs.current[di] = el)}
                   data-testid={`tg-col-${format(d, "yyyy-MM-dd")}`}
                   onPointerDown={bgPointerDown(di)}
                   className="flex-1 min-w-0 relative border-l border-slate-100" style={{ width: colW }}>
                {/* hour lines */}
                {hours.map((h, i) => (
                  <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: i * 60 * PX }} />
                ))}
                {/* current time line */}
                {today && nowMin >= 0 && nowMin <= TOTAL_MIN && (
                  <div data-testid="tg-now-line" className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: nowMin * PX }}>
                    <div className="h-[2px] bg-red-500" />
                    <div className="absolute -left-[6px] -top-[5px] w-3 h-3 rounded-full bg-red-500" />
                  </div>
                )}
                {/* event blocks */}
                {packed.map(({ e, s, col, ncols }) => {
                  const dur = (e.durata_min || 60);
                  const c = colorOf(e);
                  const Ico = iconFor(e);
                  const top = s * PX;
                  const height = Math.max(dur * PX, 18);
                  const widthPct = 100 / ncols;
                  const editable = canEdit && canEdit(e);
                  const txt = isLightColor(c) ? "#1f2937" : "#ffffff";
                  const ini = nameById ? initialsOf(nameById[e.owner_agent_id] || e.owner_name) : "";
                  return (
                    <div key={e.id} data-testid={`tg-event-${e.id}`}
                         onPointerDown={blockPointerDown(e, di, s, dur)} draggable={false} onDragStart={(ev) => ev.preventDefault()}
                         title={`${hhmm(s)}–${hhmm(s + dur)} · ${e.titolo}`}
                         className="absolute rounded-lg py-1 overflow-hidden text-left shadow-[0_2px_8px_rgba(43,43,43,0.12)] hover:shadow-[0_6px_16px_rgba(43,43,43,0.2)] hover:z-30 transition-shadow z-10 select-none ring-1 ring-white"
                         style={{
                           top: top + 1, height: height - 2, left: `calc(${col * widthPct}% + 2px)`, width: `calc(${ncols > 1 ? Math.min(100 - col * widthPct, widthPct * 1.7) : widthPct}% - 6px)`, zIndex: 10 + col,
                           background: c, cursor: editable ? "grab" : "pointer", paddingLeft: ncols > 1 ? 4 : 8, paddingRight: ncols > 1 ? 4 : 8,
                         }}>
                      <div className="flex items-center gap-1 min-w-0" style={{ color: txt }}>
                        <span className="text-xs font-bold truncate whitespace-nowrap leading-tight min-w-0">{e.titolo}{height <= 30 && ncols === 1 && <span className="font-normal opacity-90">, {hhmm(s)}</span>}</span>
                        {ini && ncols < 3 && <span className="ml-auto shrink-0 text-[9px] font-bold rounded px-1" data-testid={`tg-ini-${e.id}`} style={{ background: "rgba(255,255,255,.32)", color: txt }}>{ini}</span>}
                      </div>
                      {(height > 30 || ncols > 1) && <div className="text-[11px] leading-tight flex items-center gap-1 whitespace-nowrap overflow-hidden" style={{ color: txt, opacity: 0.9 }}>{ncols === 1 && <Ico size={10} className="shrink-0" />}{hhmm(s)}{ncols === 1 && <> – {hhmm(s + dur)}</>}</div>}
                      {height > 56 && !e.masked && (e.immobile_titolo || e.luogo) && <div className="text-[11px] truncate" style={{ color: txt, opacity: 0.85 }}>{e.immobile_titolo || e.luogo}</div>}
                      {editable && (
                        <div data-testid={`tg-resize-${e.id}`} onPointerDown={resizePointerDown(e, di, s, dur)}
                             className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize" style={{ touchAction: "none" }} />
                      )}
                    </div>
                  );
                })}
                {/* drag ghost */}
                {drag && drag.moved && drag.dayIndex === di && drag.kind !== "click" && (
                  <div data-testid="tg-ghost" className="absolute rounded-lg z-30 pointer-events-none border-2 border-dashed border-[#FC82ED] bg-[#FC82ED]/20"
                       style={{ top: drag.startMin * PX, height: Math.max((drag.endMin - drag.startMin) * PX, 16), left: 2, right: 2 }}>
                    <div className="text-[10px] font-bold text-[#b3119e] px-1 mono">{hhmm(drag.startMin)}–{hhmm(drag.endMin)}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
